// Mock data for Analytics pages.
// Shape mirrors the real API response: { kpis, items, trendOverTime }
//
// kpis:
//   total          — number of distinct items (specialties / scripts / etc.)
//   avgAccuracy    — overall average accuracy across all items (%)
//   strongest      — { name, accuracy } of the best-performing item
//   weakest        — { name, accuracy } of the worst-performing item
//   passingCount   — items with accuracy >= 60
//   totalQuestions — cumulative questions answered
//
// items:
//   name, accuracy, correct, incorrect, total
//   (sorted best → worst)
//
// trendOverTime:
//   { month, avgAccuracy }  — monthly average, oldest first

const TREND_6M = [
  { month: "Nov 2025", avgAccuracy: 61.0 },
  { month: "Dec 2025", avgAccuracy: 64.5 },
  { month: "Jan 2026", avgAccuracy: 68.2 },
  { month: "Feb 2026", avgAccuracy: 72.8 },
  { month: "Mar 2026", avgAccuracy: 76.1 },
  { month: "Apr 2026", avgAccuracy: 79.4 },
];

// ── By Specialty ─────────────────────────────────────────────────────────────
export const mockAnalyticsSpecialty = {
  kpis: {
    total: 6,
    avgAccuracy: 71.0,
    strongest: { name: "Cardiology", accuracy: 86 },
    weakest: { name: "Anatomy", accuracy: 34 },
    passingCount: 4,
    totalQuestions: 204,
  },
  items: [
    { name: "Cardiology",    accuracy: 86, correct: 43, incorrect: 7,  total: 50 },
    { name: "Pharmacology",  accuracy: 78, correct: 39, incorrect: 11, total: 50 },
    { name: "Physiology",    accuracy: 72, correct: 36, incorrect: 14, total: 50 },
    { name: "Pathology",     accuracy: 62, correct: 31, incorrect: 19, total: 50 },
    { name: "Microbiology",  accuracy: 44, correct: 2,  incorrect: 3,  total: 4  },
    { name: "Anatomy",       accuracy: 34, correct: 17, incorrect: 33, total: 50 },
  ],
  trendOverTime: TREND_6M,
};

// ── By Script ────────────────────────────────────────────────────────────────
export const mockAnalyticsScript = {
  kpis: {
    total: 5,
    avgAccuracy: 69.4,
    strongest: { name: "Heart Failure Basics", accuracy: 88 },
    weakest: { name: "Renal Pathology", accuracy: 41 },
    passingCount: 3,
    totalQuestions: 175,
  },
  items: [
    { name: "Heart Failure Basics",    accuracy: 88, correct: 44, incorrect: 6,  total: 50 },
    { name: "Respiratory Pharmacology",accuracy: 74, correct: 37, incorrect: 13, total: 50 },
    { name: "Neuro Case Studies",      accuracy: 68, correct: 34, incorrect: 16, total: 50 },
    { name: "GI Physiology",           accuracy: 56, correct: 14, incorrect: 11, total: 25 },
    { name: "Renal Pathology",         accuracy: 41, correct: 20, incorrect: 29, total: 49 },  // skip 1 intentionally
  ],
  trendOverTime: [
    { month: "Nov 2025", avgAccuracy: 58.0 },
    { month: "Dec 2025", avgAccuracy: 62.5 },
    { month: "Jan 2026", avgAccuracy: 67.0 },
    { month: "Feb 2026", avgAccuracy: 70.8 },
    { month: "Mar 2026", avgAccuracy: 74.2 },
    { month: "Apr 2026", avgAccuracy: 77.5 },
  ],
};

// ── By Topic ─────────────────────────────────────────────────────────────────
export const mockAnalyticsTopic = {
  kpis: {
    total: 7,
    avgAccuracy: 66.7,
    strongest: { name: "Drug Mechanisms", accuracy: 91 },
    weakest: { name: "Embryology", accuracy: 29 },
    passingCount: 4,
    totalQuestions: 231,
  },
  items: [
    { name: "Drug Mechanisms",    accuracy: 91, correct: 46, incorrect: 4,  total: 50 },
    { name: "Cardiac Output",     accuracy: 82, correct: 41, incorrect: 9,  total: 50 },
    { name: "Inflammation",       accuracy: 74, correct: 37, incorrect: 13, total: 50 },
    { name: "Haemostasis",        accuracy: 63, correct: 25, incorrect: 15, total: 40 },
    { name: "Renal Regulation",   accuracy: 55, correct: 28, incorrect: 23, total: 51 },
    { name: "Neurophysiology",    accuracy: 47, correct: 23, incorrect: 26, total: 49 },
    { name: "Embryology",         accuracy: 29, correct: 12, incorrect: 29, total: 41 },
  ],
  trendOverTime: [
    { month: "Nov 2025", avgAccuracy: 55.0 },
    { month: "Dec 2025", avgAccuracy: 59.0 },
    { month: "Jan 2026", avgAccuracy: 63.5 },
    { month: "Feb 2026", avgAccuracy: 67.2 },
    { month: "Mar 2026", avgAccuracy: 71.0 },
    { month: "Apr 2026", avgAccuracy: 75.8 },
  ],
};

// ── By Explanation ───────────────────────────────────────────────────────────
export const mockAnalyticsExplanation = {
  kpis: {
    total: 4,
    avgAccuracy: 73.5,
    strongest: { name: "Detailed Diagrams",  accuracy: 89 },
    weakest: { name: "Text Only",            accuracy: 52 },
    passingCount: 3,
    totalQuestions: 142,
  },
  items: [
    { name: "Detailed Diagrams",  accuracy: 89, correct: 45, incorrect: 6,  total: 51 },
    { name: "Mnemonics",          accuracy: 78, correct: 39, incorrect: 11, total: 50 },
    { name: "Case-Based",         accuracy: 75, correct: 30, incorrect: 10, total: 40 },
    { name: "Text Only",          accuracy: 52, correct: 26, incorrect: 25, total: 51 },
  ],
  trendOverTime: [
    { month: "Nov 2025", avgAccuracy: 63.0 },
    { month: "Dec 2025", avgAccuracy: 67.5 },
    { month: "Jan 2026", avgAccuracy: 70.0 },
    { month: "Feb 2026", avgAccuracy: 74.5 },
    { month: "Mar 2026", avgAccuracy: 77.0 },
    { month: "Apr 2026", avgAccuracy: 80.2 },
  ],
};

// Lookup map used by AnalyticsPage
export const ANALYTICS_MOCK = {
  specialty:   mockAnalyticsSpecialty,
  script:      mockAnalyticsScript,
  topic:       mockAnalyticsTopic,
  explanation: mockAnalyticsExplanation,
};

// ─────────────────────────────────────────────────────────────────────────────
// Drill-down mock data
// Shape mirrors POST /analytics/v1/:attribute/:item:
//   { kpis, quizHistory, trendOverTime }
//
// Each quiz in quizHistory now includes:
//   quizId      — links to /user/quiz/result/:quizId
//   questions[] — sample questions shown for this quiz
//     { id, text, isCorrect, yourAnswer, correctAnswer }
//     id links to /script-questions/detail/:id
// ─────────────────────────────────────────────────────────────────────────────

// Generate sample questions from a pool, marking correct/incorrect to match accuracy
const makeQuestions = (pool, accuracy, quizSeed) => {
  const count      = Math.min(8, pool.length);
  const correctCnt = Math.round(count * (accuracy / 100));
  return pool.slice(quizSeed % Math.max(1, pool.length - count), (quizSeed % Math.max(1, pool.length - count)) + count)
    .slice(0, count)
    .map((q, i) => ({ ...q, isCorrect: i < correctCnt }));
};

const buildDetail = (quizHistory) => {
  const accuracies = quizHistory.map((q) => q.accuracy);
  const passing    = quizHistory.filter((q) => q.passed);
  return {
    kpis: {
      totalQuizzes:   quizHistory.length,
      avgAccuracy:    Math.round(accuracies.reduce((s, a) => s + a, 0) / accuracies.length),
      personalBest:   Math.max(...accuracies),
      totalQuestions: quizHistory.reduce((s, q) => s + q.total, 0),
      passRate:       Math.round((passing.length / quizHistory.length) * 100),
      latestScore:    quizHistory[quizHistory.length - 1]?.accuracy ?? 0,
    },
    quizHistory,
    trendOverTime: quizHistory.map((q) => ({ date: q.date, accuracy: q.accuracy })),
  };
};

// ── Question pools ────────────────────────────────────────────────────────────

const CARDIOLOGY_QS = [
  { id: "sq-crd-001", text: "Which mechanism best explains increased stroke volume with increased preload?", yourAnswer: "Frank-Starling mechanism", correctAnswer: "Frank-Starling mechanism" },
  { id: "sq-crd-002", text: "Most common cause of heart failure in developed countries?", yourAnswer: "Hypertension", correctAnswer: "Ischaemic heart disease" },
  { id: "sq-crd-003", text: "A 65-year-old presents with orthopnea and bilateral basal crackles. Most likely diagnosis?", yourAnswer: "Left ventricular failure", correctAnswer: "Left ventricular failure" },
  { id: "sq-crd-004", text: "First-line pharmacological treatment for stable angina?", yourAnswer: "Short-acting nitrate", correctAnswer: "Short-acting nitrate" },
  { id: "sq-crd-005", text: "ECG shows delta waves and shortened PR interval. Diagnosis?", yourAnswer: "Wolff-Parkinson-White syndrome", correctAnswer: "Wolff-Parkinson-White syndrome" },
  { id: "sq-crd-006", text: "Which drug class reduces mortality in chronic heart failure with reduced ejection fraction?", yourAnswer: "Beta-blockers", correctAnswer: "Beta-blockers" },
  { id: "sq-crd-007", text: "Normal range for left ventricular ejection fraction?", yourAnswer: "40–55%", correctAnswer: "55–70%" },
  { id: "sq-crd-008", text: "Tricuspid valve endocarditis is most associated with which risk factor?", yourAnswer: "IV drug use", correctAnswer: "IV drug use" },
  { id: "sq-crd-009", text: "Which enzyme is most specific for myocardial infarction?", yourAnswer: "Troponin I", correctAnswer: "Troponin I" },
  { id: "sq-crd-010", text: "Mechanism of action of digoxin?", yourAnswer: "Na/K-ATPase inhibition", correctAnswer: "Na/K-ATPase inhibition" },
];

const PHARMACOLOGY_QS = [
  { id: "sq-phr-001", text: "Mechanism of action of beta-lactam antibiotics?", yourAnswer: "Cell wall synthesis inhibition", correctAnswer: "Cell wall synthesis inhibition" },
  { id: "sq-phr-002", text: "Which drug is contraindicated with MAO inhibitors?", yourAnswer: "Pethidine", correctAnswer: "Pethidine" },
  { id: "sq-phr-003", text: "First-pass metabolism primarily occurs in which organ?", yourAnswer: "Kidney", correctAnswer: "Liver" },
  { id: "sq-phr-004", text: "Zero-order kinetics means?", yourAnswer: "Fixed amount eliminated per unit time", correctAnswer: "Fixed amount eliminated per unit time" },
  { id: "sq-phr-005", text: "Which anticoagulant requires monitoring via INR?", yourAnswer: "Warfarin", correctAnswer: "Warfarin" },
  { id: "sq-phr-006", text: "Flumazenil antagonises which drug class?", yourAnswer: "Benzodiazepines", correctAnswer: "Benzodiazepines" },
  { id: "sq-phr-007", text: "Which NSAID is most COX-2 selective?", yourAnswer: "Celecoxib", correctAnswer: "Celecoxib" },
  { id: "sq-phr-008", text: "Metformin mechanism of action?", yourAnswer: "AMPK activation / decreased hepatic glucose output", correctAnswer: "AMPK activation / decreased hepatic glucose output" },
];

const ANATOMY_QS = [
  { id: "sq-ant-001", text: "Which nerve is responsible for wrist drop?", yourAnswer: "Median nerve", correctAnswer: "Radial nerve" },
  { id: "sq-ant-002", text: "The femoral triangle is bounded superiorly by?", yourAnswer: "Inguinal ligament", correctAnswer: "Inguinal ligament" },
  { id: "sq-ant-003", text: "Which muscle is the primary flexor of the forearm?", yourAnswer: "Biceps brachii", correctAnswer: "Biceps brachii" },
  { id: "sq-ant-004", text: "The thoracic duct drains into which vessel?", yourAnswer: "Right subclavian vein", correctAnswer: "Left subclavian vein" },
  { id: "sq-ant-005", text: "McBurney's point corresponds to which structure?", yourAnswer: "Appendix", correctAnswer: "Appendix" },
  { id: "sq-ant-006", text: "Which foramen transmits CN X (vagus nerve)?", yourAnswer: "Jugular foramen", correctAnswer: "Jugular foramen" },
  { id: "sq-ant-007", text: "The portal triad in the liver contains?", yourAnswer: "Portal vein, hepatic artery, bile duct", correctAnswer: "Portal vein, hepatic artery, bile duct" },
  { id: "sq-ant-008", text: "Which vertebral level does the spinal cord typically end in adults?", yourAnswer: "L4", correctAnswer: "L1–L2" },
];

const GENERIC_QS = (prefix) => [
  { id: `${prefix}-001`, text: "Which is the correct primary mechanism described in this module?", yourAnswer: "Option A", correctAnswer: "Option A" },
  { id: `${prefix}-002`, text: "A patient presents with classic findings. What is the diagnosis?", yourAnswer: "Correct diagnosis", correctAnswer: "Correct diagnosis" },
  { id: `${prefix}-003`, text: "Which investigation would you order first?", yourAnswer: "Blood tests", correctAnswer: "ECG" },
  { id: `${prefix}-004`, text: "First-line management for this condition?", yourAnswer: "Correct management", correctAnswer: "Correct management" },
  { id: `${prefix}-005`, text: "Which complication is most common?", yourAnswer: "Correct complication", correctAnswer: "Correct complication" },
  { id: `${prefix}-006`, text: "What is the mechanism of the pathology described?", yourAnswer: "Correct mechanism", correctAnswer: "Correct mechanism" },
  { id: `${prefix}-007`, text: "Which drug class is contraindicated in this scenario?", yourAnswer: "Incorrect class", correctAnswer: "Correct class" },
  { id: `${prefix}-008`, text: "What is the correct normal reference range?", yourAnswer: "Correct range", correctAnswer: "Correct range" },
];

// ── Specialty detail ─────────────────────────────────────────────────────────
const specialtyDetail = {
  Cardiology: buildDetail([
    { date: "2025-11-10", quizId: "qz-crd-001", quizName: "Cardiology Fundamentals",  accuracy: 72, correct: 36, total: 50, passed: true,  questions: makeQuestions(CARDIOLOGY_QS, 72, 0) },
    { date: "2025-11-24", quizId: "qz-crd-002", quizName: "Heart Failure",            accuracy: 78, correct: 39, total: 50, passed: true,  questions: makeQuestions(CARDIOLOGY_QS, 78, 1) },
    { date: "2025-12-08", quizId: "qz-crd-003", quizName: "Arrhythmias",              accuracy: 82, correct: 41, total: 50, passed: true,  questions: makeQuestions(CARDIOLOGY_QS, 82, 2) },
    { date: "2025-12-22", quizId: "qz-crd-004", quizName: "Cardiology Basics II",     accuracy: 80, correct: 40, total: 50, passed: true,  questions: makeQuestions(CARDIOLOGY_QS, 80, 0) },
    { date: "2026-01-12", quizId: "qz-crd-005", quizName: "Valvular Disease",         accuracy: 88, correct: 44, total: 50, passed: true,  questions: makeQuestions(CARDIOLOGY_QS, 88, 1) },
    { date: "2026-02-02", quizId: "qz-crd-006", quizName: "Cardiac Output",           accuracy: 90, correct: 45, total: 50, passed: true,  questions: makeQuestions(CARDIOLOGY_QS, 90, 2) },
    { date: "2026-02-28", quizId: "qz-crd-007", quizName: "Cardiology Advanced",      accuracy: 96, correct: 48, total: 50, passed: true,  questions: makeQuestions(CARDIOLOGY_QS, 96, 0) },
    { date: "2026-03-20", quizId: "qz-crd-008", quizName: "Cardiology Review",        accuracy: 84, correct: 42, total: 50, passed: true,  questions: makeQuestions(CARDIOLOGY_QS, 84, 1) },
  ]),
  Pharmacology: buildDetail([
    { date: "2025-11-15", quizId: "qz-phr-001", quizName: "Drug Classes",             accuracy: 62, correct: 31, total: 50, passed: true,  questions: makeQuestions(PHARMACOLOGY_QS, 62, 0) },
    { date: "2025-12-01", quizId: "qz-phr-002", quizName: "Pharmacokinetics",         accuracy: 70, correct: 35, total: 50, passed: true,  questions: makeQuestions(PHARMACOLOGY_QS, 70, 1) },
    { date: "2025-12-20", quizId: "qz-phr-003", quizName: "Antibiotics",              accuracy: 76, correct: 38, total: 50, passed: true,  questions: makeQuestions(PHARMACOLOGY_QS, 76, 0) },
    { date: "2026-01-18", quizId: "qz-phr-004", quizName: "Drug Interactions",        accuracy: 80, correct: 40, total: 50, passed: true,  questions: makeQuestions(PHARMACOLOGY_QS, 80, 1) },
    { date: "2026-02-14", quizId: "qz-phr-005", quizName: "Pharmacology Review",      accuracy: 82, correct: 41, total: 50, passed: true,  questions: makeQuestions(PHARMACOLOGY_QS, 82, 0) },
  ]),
  Physiology: buildDetail([
    { date: "2025-11-20", quizId: "qz-phy-001", quizName: "Cell Physiology",          accuracy: 58, correct: 29, total: 50, passed: false, questions: makeQuestions(GENERIC_QS("sq-phy"), 58, 0) },
    { date: "2025-12-10", quizId: "qz-phy-002", quizName: "Cardiac Physiology",       accuracy: 68, correct: 34, total: 50, passed: true,  questions: makeQuestions(GENERIC_QS("sq-phy"), 68, 1) },
    { date: "2026-01-05", quizId: "qz-phy-003", quizName: "Renal Physiology",         accuracy: 72, correct: 36, total: 50, passed: true,  questions: makeQuestions(GENERIC_QS("sq-phy"), 72, 0) },
    { date: "2026-02-10", quizId: "qz-phy-004", quizName: "Respiratory Physiology",   accuracy: 76, correct: 38, total: 50, passed: true,  questions: makeQuestions(GENERIC_QS("sq-phy"), 76, 1) },
    { date: "2026-03-05", quizId: "qz-phy-005", quizName: "Physiology Final",         accuracy: 80, correct: 40, total: 50, passed: true,  questions: makeQuestions(GENERIC_QS("sq-phy"), 80, 0) },
  ]),
  Pathology: buildDetail([
    { date: "2025-12-05", quizId: "qz-pat-001", quizName: "Cell Injury",              accuracy: 52, correct: 26, total: 50, passed: false, questions: makeQuestions(GENERIC_QS("sq-pat"), 52, 0) },
    { date: "2026-01-08", quizId: "qz-pat-002", quizName: "Inflammation",             accuracy: 60, correct: 30, total: 50, passed: true,  questions: makeQuestions(GENERIC_QS("sq-pat"), 60, 1) },
    { date: "2026-02-05", quizId: "qz-pat-003", quizName: "Neoplasia",                accuracy: 64, correct: 32, total: 50, passed: true,  questions: makeQuestions(GENERIC_QS("sq-pat"), 64, 0) },
    { date: "2026-03-10", quizId: "qz-pat-004", quizName: "Pathology Review",         accuracy: 72, correct: 36, total: 50, passed: true,  questions: makeQuestions(GENERIC_QS("sq-pat"), 72, 1) },
  ]),
  Microbiology: buildDetail([
    { date: "2026-01-20", quizId: "qz-mic-001", quizName: "Bacteriology Basics",      accuracy: 40, correct: 2,  total: 4,  passed: false, questions: makeQuestions(GENERIC_QS("sq-mic"), 40, 0) },
    { date: "2026-03-01", quizId: "qz-mic-002", quizName: "Virology",                 accuracy: 50, correct: 10, total: 20, passed: false, questions: makeQuestions(GENERIC_QS("sq-mic"), 50, 1) },
  ]),
  Anatomy: buildDetail([
    { date: "2025-11-05", quizId: "qz-ant-001", quizName: "Upper Limb Anatomy",       accuracy: 28, correct: 14, total: 50, passed: false, questions: makeQuestions(ANATOMY_QS, 28, 0) },
    { date: "2025-12-15", quizId: "qz-ant-002", quizName: "Lower Limb Anatomy",       accuracy: 32, correct: 16, total: 50, passed: false, questions: makeQuestions(ANATOMY_QS, 32, 1) },
    { date: "2026-01-25", quizId: "qz-ant-003", quizName: "Thorax Anatomy",           accuracy: 36, correct: 18, total: 50, passed: false, questions: makeQuestions(ANATOMY_QS, 36, 0) },
    { date: "2026-03-12", quizId: "qz-ant-004", quizName: "Head & Neck",              accuracy: 40, correct: 20, total: 50, passed: false, questions: makeQuestions(ANATOMY_QS, 40, 1) },
  ]),
};

// ── Script detail ────────────────────────────────────────────────────────────
const scriptDetail = {
  "Heart Failure Basics": buildDetail([
    { date: "2025-11-12", quizId: "qz-hfb-001", quizName: "Heart Failure Basics – Set 1", accuracy: 80, correct: 40, total: 50, passed: true,  questions: makeQuestions(CARDIOLOGY_QS, 80, 0) },
    { date: "2025-12-18", quizId: "qz-hfb-002", quizName: "Heart Failure Basics – Set 2", accuracy: 86, correct: 43, total: 50, passed: true,  questions: makeQuestions(CARDIOLOGY_QS, 86, 1) },
    { date: "2026-01-30", quizId: "qz-hfb-003", quizName: "Heart Failure Basics – Set 3", accuracy: 90, correct: 45, total: 50, passed: true,  questions: makeQuestions(CARDIOLOGY_QS, 90, 2) },
    { date: "2026-03-08", quizId: "qz-hfb-004", quizName: "Heart Failure Basics – Set 4", accuracy: 94, correct: 47, total: 50, passed: true,  questions: makeQuestions(CARDIOLOGY_QS, 94, 0) },
  ]),
  "Respiratory Pharmacology": buildDetail([
    { date: "2025-11-22", quizId: "qz-rph-001", quizName: "Resp Pharm – Bronchodilators", accuracy: 64, correct: 32, total: 50, passed: true,  questions: makeQuestions(PHARMACOLOGY_QS, 64, 0) },
    { date: "2025-12-28", quizId: "qz-rph-002", quizName: "Resp Pharm – Corticosteroids", accuracy: 72, correct: 36, total: 50, passed: true,  questions: makeQuestions(PHARMACOLOGY_QS, 72, 1) },
    { date: "2026-02-15", quizId: "qz-rph-003", quizName: "Resp Pharm – Review",          accuracy: 82, correct: 41, total: 50, passed: true,  questions: makeQuestions(PHARMACOLOGY_QS, 82, 0) },
  ]),
  "Neuro Case Studies": buildDetail([
    { date: "2025-12-02", quizId: "qz-ncs-001", quizName: "Neuro Cases – Stroke",         accuracy: 60, correct: 30, total: 50, passed: true,  questions: makeQuestions(GENERIC_QS("sq-ncs"), 60, 0) },
    { date: "2026-01-14", quizId: "qz-ncs-002", quizName: "Neuro Cases – Seizures",       accuracy: 66, correct: 33, total: 50, passed: true,  questions: makeQuestions(GENERIC_QS("sq-ncs"), 66, 1) },
    { date: "2026-02-20", quizId: "qz-ncs-003", quizName: "Neuro Cases – Headache",       accuracy: 72, correct: 36, total: 50, passed: true,  questions: makeQuestions(GENERIC_QS("sq-ncs"), 72, 0) },
    { date: "2026-03-15", quizId: "qz-ncs-004", quizName: "Neuro Cases – Review",         accuracy: 76, correct: 38, total: 50, passed: true,  questions: makeQuestions(GENERIC_QS("sq-ncs"), 76, 1) },
  ]),
  "GI Physiology": buildDetail([
    { date: "2026-01-10", quizId: "qz-gip-001", quizName: "GI Physiology – Absorption",   accuracy: 48, correct: 12, total: 25, passed: false, questions: makeQuestions(GENERIC_QS("sq-gip"), 48, 0) },
    { date: "2026-02-25", quizId: "qz-gip-002", quizName: "GI Physiology – Motility",     accuracy: 64, correct: 16, total: 25, passed: true,  questions: makeQuestions(GENERIC_QS("sq-gip"), 64, 1) },
  ]),
  "Renal Pathology": buildDetail([
    { date: "2025-11-28", quizId: "qz-rpt-001", quizName: "Renal Pathology – Glomerular", accuracy: 36, correct: 18, total: 50, passed: false, questions: makeQuestions(GENERIC_QS("sq-rpt"), 36, 0) },
    { date: "2026-01-02", quizId: "qz-rpt-002", quizName: "Renal Pathology – Tubular",    accuracy: 40, correct: 20, total: 50, passed: false, questions: makeQuestions(GENERIC_QS("sq-rpt"), 40, 1) },
    { date: "2026-02-18", quizId: "qz-rpt-003", quizName: "Renal Pathology – Review",     accuracy: 46, correct: 23, total: 50, passed: false, questions: makeQuestions(GENERIC_QS("sq-rpt"), 46, 0) },
  ]),
};

// ── Topic detail ─────────────────────────────────────────────────────────────
const topicDetail = {
  "Drug Mechanisms": buildDetail([
    { date: "2025-11-08", quizId: "qz-dm-001", quizName: "Drug Mechanisms – Part 1", accuracy: 82, correct: 41, total: 50, passed: true,  questions: makeQuestions(PHARMACOLOGY_QS, 82, 0) },
    { date: "2025-12-14", quizId: "qz-dm-002", quizName: "Drug Mechanisms – Part 2", accuracy: 88, correct: 44, total: 50, passed: true,  questions: makeQuestions(PHARMACOLOGY_QS, 88, 1) },
    { date: "2026-01-22", quizId: "qz-dm-003", quizName: "Drug Mechanisms – Part 3", accuracy: 92, correct: 46, total: 50, passed: true,  questions: makeQuestions(PHARMACOLOGY_QS, 92, 0) },
    { date: "2026-03-02", quizId: "qz-dm-004", quizName: "Drug Mechanisms – Review", accuracy: 96, correct: 48, total: 50, passed: true,  questions: makeQuestions(PHARMACOLOGY_QS, 96, 1) },
  ]),
  "Cardiac Output": buildDetail([
    { date: "2025-11-18", quizId: "qz-co-001", quizName: "Cardiac Output – Basics",   accuracy: 74, correct: 37, total: 50, passed: true, questions: makeQuestions(CARDIOLOGY_QS, 74, 0) },
    { date: "2025-12-26", quizId: "qz-co-002", quizName: "Cardiac Output – Advanced", accuracy: 80, correct: 40, total: 50, passed: true, questions: makeQuestions(CARDIOLOGY_QS, 80, 1) },
    { date: "2026-02-08", quizId: "qz-co-003", quizName: "Cardiac Output – Review",   accuracy: 88, correct: 44, total: 50, passed: true, questions: makeQuestions(CARDIOLOGY_QS, 88, 0) },
  ]),
  Inflammation: buildDetail([
    { date: "2025-12-04", quizId: "qz-inf-001", quizName: "Acute Inflammation",       accuracy: 66, correct: 33, total: 50, passed: true,  questions: makeQuestions(GENERIC_QS("sq-inf"), 66, 0) },
    { date: "2026-01-16", quizId: "qz-inf-002", quizName: "Chronic Inflammation",     accuracy: 72, correct: 36, total: 50, passed: true,  questions: makeQuestions(GENERIC_QS("sq-inf"), 72, 1) },
    { date: "2026-02-22", quizId: "qz-inf-003", quizName: "Inflammation – Review",    accuracy: 80, correct: 40, total: 50, passed: true,  questions: makeQuestions(GENERIC_QS("sq-inf"), 80, 0) },
  ]),
  Haemostasis: buildDetail([
    { date: "2026-01-06", quizId: "qz-hae-001", quizName: "Coagulation Cascade",      accuracy: 55, correct: 22, total: 40, passed: false, questions: makeQuestions(GENERIC_QS("sq-hae"), 55, 0) },
    { date: "2026-02-12", quizId: "qz-hae-002", quizName: "Platelet Function",        accuracy: 70, correct: 28, total: 40, passed: true,  questions: makeQuestions(GENERIC_QS("sq-hae"), 70, 1) },
  ]),
  "Renal Regulation": buildDetail([
    { date: "2025-12-08", quizId: "qz-rr-001", quizName: "Fluid Balance",             accuracy: 48, correct: 24, total: 50, passed: false, questions: makeQuestions(GENERIC_QS("sq-rr"), 48, 0) },
    { date: "2026-01-20", quizId: "qz-rr-002", quizName: "Electrolyte Regulation",   accuracy: 54, correct: 27, total: 50, passed: false, questions: makeQuestions(GENERIC_QS("sq-rr"), 54, 1) },
    { date: "2026-03-06", quizId: "qz-rr-003", quizName: "Renal Regulation Review",  accuracy: 62, correct: 31, total: 50, passed: true,  questions: makeQuestions(GENERIC_QS("sq-rr"), 62, 0) },
  ]),
  Neurophysiology: buildDetail([
    { date: "2026-01-12", quizId: "qz-np-001", quizName: "Action Potentials",        accuracy: 42, correct: 21, total: 50, passed: false, questions: makeQuestions(GENERIC_QS("sq-np"), 42, 0) },
    { date: "2026-02-28", quizId: "qz-np-002", quizName: "Synaptic Transmission",    accuracy: 52, correct: 26, total: 50, passed: false, questions: makeQuestions(GENERIC_QS("sq-np"), 52, 1) },
  ]),
  Embryology: buildDetail([
    { date: "2025-11-30", quizId: "qz-emb-001", quizName: "Embryology – Week 1-4",   accuracy: 24, correct: 12, total: 50, passed: false, questions: makeQuestions(ANATOMY_QS, 24, 0) },
    { date: "2026-01-28", quizId: "qz-emb-002", quizName: "Embryology – Week 5-8",   accuracy: 30, correct: 15, total: 50, passed: false, questions: makeQuestions(ANATOMY_QS, 30, 1) },
    { date: "2026-03-18", quizId: "qz-emb-003", quizName: "Embryology – Review",     accuracy: 34, correct: 17, total: 50, passed: false, questions: makeQuestions(ANATOMY_QS, 34, 0) },
  ]),
};

// ── Explanation detail ───────────────────────────────────────────────────────
const explanationDetail = {
  "Detailed Diagrams": buildDetail([
    { date: "2025-11-14", quizId: "qz-dd-001", quizName: "Diagrams – Cardiovascular", accuracy: 82, correct: 42, total: 51, passed: true, questions: makeQuestions(CARDIOLOGY_QS,  82, 0) },
    { date: "2025-12-16", quizId: "qz-dd-002", quizName: "Diagrams – Respiratory",    accuracy: 86, correct: 43, total: 50, passed: true, questions: makeQuestions(CARDIOLOGY_QS,  86, 1) },
    { date: "2026-01-24", quizId: "qz-dd-003", quizName: "Diagrams – Neurological",   accuracy: 90, correct: 46, total: 51, passed: true, questions: makeQuestions(CARDIOLOGY_QS,  90, 2) },
    { date: "2026-03-04", quizId: "qz-dd-004", quizName: "Diagrams – Review",         accuracy: 96, correct: 48, total: 50, passed: true, questions: makeQuestions(CARDIOLOGY_QS,  96, 0) },
  ]),
  Mnemonics: buildDetail([
    { date: "2025-12-10", quizId: "qz-mn-001", quizName: "Mnemonics – Drug Names",    accuracy: 72, correct: 36, total: 50, passed: true, questions: makeQuestions(PHARMACOLOGY_QS, 72, 0) },
    { date: "2026-01-26", quizId: "qz-mn-002", quizName: "Mnemonics – Anatomy",       accuracy: 78, correct: 39, total: 50, passed: true, questions: makeQuestions(PHARMACOLOGY_QS, 78, 1) },
    { date: "2026-03-10", quizId: "qz-mn-003", quizName: "Mnemonics – Review",        accuracy: 84, correct: 42, total: 50, passed: true, questions: makeQuestions(PHARMACOLOGY_QS, 84, 0) },
  ]),
  "Case-Based": buildDetail([
    { date: "2025-11-26", quizId: "qz-cb-001", quizName: "Case Study – Cardiology",   accuracy: 68, correct: 27, total: 40, passed: true, questions: makeQuestions(CARDIOLOGY_QS,  68, 0) },
    { date: "2026-01-04", quizId: "qz-cb-002", quizName: "Case Study – Pulmonology",  accuracy: 78, correct: 31, total: 40, passed: true, questions: makeQuestions(CARDIOLOGY_QS,  78, 1) },
    { date: "2026-02-16", quizId: "qz-cb-003", quizName: "Case Study – Nephrology",   accuracy: 80, correct: 32, total: 40, passed: true, questions: makeQuestions(CARDIOLOGY_QS,  80, 2) },
  ]),
  "Text Only": buildDetail([
    { date: "2025-12-20", quizId: "qz-to-001", quizName: "Text Only – Pharmacology",  accuracy: 46, correct: 23, total: 50, passed: false, questions: makeQuestions(PHARMACOLOGY_QS, 46, 0) },
    { date: "2026-01-30", quizId: "qz-to-002", quizName: "Text Only – Physiology",    accuracy: 52, correct: 26, total: 50, passed: false, questions: makeQuestions(PHARMACOLOGY_QS, 52, 1) },
    { date: "2026-03-14", quizId: "qz-to-003", quizName: "Text Only – Review",        accuracy: 58, correct: 29, total: 50, passed: false, questions: makeQuestions(PHARMACOLOGY_QS, 58, 0) },
  ]),
};

// Lookup map used by AnalyticsDetailPage
export const ANALYTICS_DETAIL_MOCK = {
  specialty:   specialtyDetail,
  script:      scriptDetail,
  topic:       topicDetail,
  explanation: explanationDetail,
};
